import React, { useState, useEffect, useRef } from 'react'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'

interface ChatInputProps {
  onSend: (input: string) => Promise<void>;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  isDefaultFlow?: boolean;
  onToolClick?: (toolTitle: string) => void;
  activeToolOverlay?: string | null;
}

export interface ToolInfo {
  title: string;
  icon: string;
  alt: string;
}

export const tools: ToolInfo[] = [
  { title: 'Open Blocks', icon: '/images/builder-pages.svg', alt: 'Open Blocks' },
  { title: 'Open Layer Manager', icon: '/images/builder-layers.svg', alt: 'Layers' },
  { title: 'Settings', icon: '/images/builder-settings.svg', alt: 'Settings' },
  { title: 'Open Style Manager', icon: '/images/builder-paint-brush.svg', alt: 'Edit' },
  { title: 'AI Content', icon: '/images/builder-utm-AI.svg', alt: 'AI Content' }
];

export const ChatInput: React.FC<ChatInputProps> = ({ 
  onSend, 
  className, 
  disabled, 
  placeholder = 'Type a message...', 
  isDefaultFlow,
  onToolClick,
  activeToolOverlay
}) => {
  const [input, setInput] = useState('');
  const [textareaHeight, setTextareaHeight] = useState('h-10');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    if (input.trim()) {
      onSend(input)
      setInput('')
      setTextareaHeight('h-10')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)

    // Auto-resize textarea based on content
    const target = e.target
    target.style.height = 'auto'

    const newHeight = Math.min(target.scrollHeight, 120)
    target.style.height = `${newHeight}px`

    if (newHeight > 40) {
      setTextareaHeight('h-auto')
    } else {
      setTextareaHeight('h-10')
    }
  }

  // Reset textarea height when input is cleared
  useEffect(() => {
    if (input === '' && textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      setTextareaHeight('h-10')
    }
  }, [input])

  // Default flow version
  if (isDefaultFlow) {
    return (
      <div className={`${className || ''} w-full p-5`}>
        <div className="w-full rounded-lg bg-[#292A2D] border border-[#3A3A3C] px-4 py-3">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyPress}
            placeholder="Ask Walter..."
            disabled={disabled || !!activeToolOverlay}
            className="w-full bg-transparent text-white placeholder-gray-400 text-base resize-none focus:outline-none"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center">
              <TooltipProvider>
                {tools.map((tool, index) => (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={() => onToolClick?.(tool.title)}
                        className={`text-gray-400 hover:text-white p-1 ${activeToolOverlay === tool.title ? 'text-white bg-[#3A3A3C] rounded' : ''}`}
                      >
                        <img src={tool.icon} alt={tool.alt} width={24} height={24} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {tool.alt}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TooltipProvider>
            </div>
            <div>
              <button
                onClick={handleSend}
                disabled={disabled || !input.trim() || !!activeToolOverlay}
                className={`rounded-full bg-[#FE6434] p-2 ${(!input.trim() || !!activeToolOverlay) ? 'opacity-50' : ''}`}
              >
                <img src="/images/builder-arrow-up.svg" alt="Send" width={20} height={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular version
  return (
    <div className={`border-t bg-white ${className || ''} min-h-[72px]`}>
      <div className="flex items-center justify-center mt-[6px]">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInput}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          className={`flex-1 text-left text-base leading-[24px] rounded-lg border-none px-4 pt-[17px] focus:outline-none resize-none
            ${disabled ? 'bg-gray-100 text-gray-500' : 'bg-white placeholder-gray-500'} 
            ${textareaHeight}`}
        />

        <button
          onClick={handleSend}
          disabled={disabled || !input.trim()}
          className="flex items-center justify-center"
        >
          <img 
            src="/images/send-button.svg" 
            alt="Send"
            width={24}
            height={24}
          />
        </button>
      </div>
    </div>
  );
};
